import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { FormButton } from 'theme/elements/Buttons';
import { FormCard } from 'theme/elements/Forms';
import type {
	ICreateSystemMessagePayload,
	ISystemMessageCompanyContact,
	SystemMessageFormMode,
} from 'types/system-messages.types';
import { CreateFormAudienceSection } from './components/CreateFormAudienceSection';
import { CreateFormContentSection } from './components/CreateFormContentSection';
import { CreateFormMetadataSection } from './components/CreateFormMetadataSection';
import { CreateFormScheduleSection } from './components/CreateFormScheduleSection';
import { getCreateFormContainerSx, getCreateFormHeaderSubtitleSx, getCreateFormHeaderTitleSx } from './createForm.styles';
import { useCreateSystemMessageForm } from './hooks/useCreateSystemMessageForm';

interface CreateFormProps {
	mode: SystemMessageFormMode;
	cardTitle: string;
	companyId?: number;
	companyContacts?: ISystemMessageCompanyContact[];
	onSubmit: (payload: ICreateSystemMessagePayload) => void;
}

export const CreateForm = ({ mode, cardTitle, companyId, companyContacts = [], onSubmit }: CreateFormProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const form = useCreateSystemMessageForm({ mode, companyId, companyContacts, onSubmit });

	return (
		<Box sx={getCreateFormContainerSx(theme)}>
			<FormCard sx={{ padding: '32px' }}>
				<Stack spacing={3}>
					<Stack spacing={0.5}>
						<Typography component="div" sx={getCreateFormHeaderTitleSx(theme)}>
							{cardTitle}
						</Typography>
						<Typography sx={getCreateFormHeaderSubtitleSx(theme)}>
							{t('page.systemMessages.createCard.subtitle')}
						</Typography>
					</Stack>

					<CreateFormMetadataSection
						internalDescription={form.internalDescription}
						onInternalDescriptionChange={form.setInternalDescription}
						internalDescriptionTooLong={form.internalDescriptionTooLong}
						iconType={form.iconType}
						onIconTypeChange={form.setIconType}
					/>

					<CreateFormContentSection
						titleHe={form.titleHe}
						onTitleHeChange={form.setTitleHe}
						titleHeTooLong={form.titleHeTooLong}
						titleEn={form.titleEn}
						onTitleEnChange={form.setTitleEn}
						titleEnTooLong={form.titleEnTooLong}
						contentHe={form.contentHe}
						onContentHeChange={form.setContentHe}
						contentHeTooLong={form.contentHeTooLong}
						contentEn={form.contentEn}
						onContentEnChange={form.setContentEn}
						contentEnTooLong={form.contentEnTooLong}
					/>

					<CreateFormAudienceSection
						mode={mode}
						audienceType={form.audienceType}
						onAudienceTypeChange={form.setAudienceType}
						contactIdsRaw={form.contactIdsRaw}
						onContactIdsRawChange={form.setContactIdsRaw}
						companyContacts={form.companyContacts}
						checkedContactIds={form.checkedContactIds}
						onCompanyContactToggle={form.handleCompanyContactToggle}
					/>

					<CreateFormScheduleSection
						dateRange={form.dateRange}
						onDateRangeChange={form.setDateRange}
						startTime={form.startTime}
						onStartTimeChange={form.setStartTime}
						endTime={form.endTime}
						onEndTimeChange={form.setEndTime}
						scheduleInvalid={form.isScheduleInvalid}
					/>
				</Stack>
			</FormCard>

			<Stack direction="row" justifyContent="flex-start" sx={{ width: '100%', mt: 3, mb: 5 }}>
				<FormButton>
					<Button variant="contained" onClick={form.handleSave} disabled={!form.canSave || form.isSaving}>
						{t('page.payment_categories.button.saveChanges')}
					</Button>
				</FormButton>
			</Stack>
		</Box>
	);
};
